/**
 * @file Renders one Home summary level's progress: the states before figures
 * exist in words, the found level through `children`. Consumers: the Home
 * tiles and Needs me, so both read an unsettled level the same way. Only the
 * not-visible tooltip differs per consumer; the spinner is labelled
 * "Loading <label>", and a missing summary reads as being rebuilt, never 0.
 */
import { FC, ReactNode } from 'react';
import { Spin, Tooltip, Typography } from 'antd';

import type { AnalyticsSummaryProgress } from '@/analytics';

const { Text } = Typography;

type FoundProgress = Extract<AnalyticsSummaryProgress, { status: 'found' }>;

export const HomeSummaryProgress: FC<{
  progress: AnalyticsSummaryProgress;
  label: string;
  notVisibleTooltip: string;
  children: (progress: FoundProgress) => ReactNode;
}> = ({ progress, label, notVisibleTooltip, children }) => {
  switch (progress.status) {
    case 'notVisible':
      return (
        <Tooltip title={notVisibleTooltip}>
          <Text type="secondary">—</Text>
        </Tooltip>
      );
    case 'noTeam':
      return <Text type="secondary">No team assigned</Text>;
    case 'loading':
      return <Spin size="small" aria-label={`Loading ${label}`} />;
    case 'notFound':
      return <Text type="secondary">Numbers are being rebuilt</Text>;
    case 'failed':
      return <Text type="danger">{progress.message}</Text>;
    case 'found':
      return children(progress);
  }
};
